import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, ShoppingBag, Sparkles, MessageCircle, TrendingUp, Crown, BadgeCheck, LogIn, ImagePlus, Tag, X, Radio, Smile, Flag, Pin } from "lucide-react";
import { toast } from "sonner";
import { API, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

const ROOM_ICONS = {
  shopping: ShoppingBag, looks: Sparkles, products: MessageCircle, trending: TrendingUp, creators: Crown,
};
const SEEN_KEY = "svk_chat_seen";
const REACTIONS = ["❤️", "😍", "🔥", "👏", "😂"];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function Avatar({ name, avatar, creator }) {
  return (
    <span className={`w-9 h-9 rounded-full grid place-items-center text-sm font-semibold shrink-0 overflow-hidden ${creator ? "ring-2 ring-[#D4AF37]" : ""} ${avatar ? "" : "bg-[#6A1B29] text-white"}`}>
      {avatar ? <img src={avatar} alt={name} className="w-full h-full object-cover" /> : (name?.[0] || "S")}
    </span>
  );
}

export default function SakhiChat() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [live, setLive] = useState([]);
  const [active, setActive] = useState("shopping");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [seen, setSeen] = useState(() => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; } });
  const [products, setProducts] = useState([]);
  const [attachImg, setAttachImg] = useState("");
  const [attachProd, setAttachProd] = useState(null);
  const [showProd, setShowProd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerFor, setPickerFor] = useState("");
  const lastRef = useRef("");
  const scrollRef = useRef(null);
  const activeRef = useRef(active);
  const fileRef = useRef(null);
  activeRef.current = active;

  const scrollToBottom = () => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; };
  const markSeen = (room, count) => {
    setSeen((prev) => { const next = { ...prev, [room]: count }; localStorage.setItem(SEEN_KEY, JSON.stringify(next)); return next; });
  };
  const loadRooms = () => API.get("/chat/rooms").then((r) => {
    setRooms(r.data);
    const cur = r.data.find((x) => x.id === activeRef.current);
    if (cur) markSeen(activeRef.current, cur.message_count);
  }).catch(() => {});
  const loadLive = () => API.get("/chat/live").then((r) => setLive(r.data || [])).catch(() => {});

  const loadMessages = async (room, incremental) => {
    try {
      const after = incremental ? lastRef.current : "";
      const r = await API.get(`/chat/${room}/messages${after ? `?after=${encodeURIComponent(after)}` : ""}`);
      const incoming = r.data.messages || [];
      if (room !== activeRef.current) return;
      if (!incremental) {
        setMessages(incoming);
        if (incoming.length) lastRef.current = incoming[incoming.length - 1].created_at;
        setTimeout(scrollToBottom, 50);
      } else if (incoming.length) {
        setMessages((prev) => {
          const have = new Set(prev.map((m) => m.id));
          const add = incoming.filter((m) => !have.has(m.id));
          return add.length ? [...prev, ...add] : prev;
        });
        lastRef.current = incoming[incoming.length - 1].created_at;
        setTimeout(scrollToBottom, 50);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { loadRooms(); loadLive(); API.get("/products?limit=100").then((r) => setProducts(r.data.items || [])).catch(() => {}); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    lastRef.current = ""; setMessages([]);
    loadMessages(active, false);
    const id = setInterval(() => loadMessages(active, true), 4000);
    const rid = setInterval(() => { loadRooms(); loadLive(); }, 15000);
    return () => { clearInterval(id); clearInterval(rid); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = process.env.REACT_APP_BACKEND_URL || "";
      setAttachImg(r.data.url.startsWith("http") ? r.data.url : `${base}${r.data.url}`);
    } catch { /* ignore */ } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if ((!body && !attachImg) || sending) return;
    setSending(true);
    try {
      const r = await API.post(`/chat/${active}/messages`, { body, image: attachImg, product_id: attachProd?.id || "" });
      setText(""); setAttachImg(""); setAttachProd(null); setShowProd(false);
      setMessages((prev) => (prev.some((m) => m.id === r.data.id) ? prev : [...prev, r.data]));
      lastRef.current = r.data.created_at;
      setTimeout(scrollToBottom, 50);
      loadRooms();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const activeRoom = rooms.find((r) => r.id === active);

  const react = async (mid, emoji) => {
    setPickerFor("");
    if (!user) return;
    try {
      const r = await API.post(`/chat/messages/${mid}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.id === mid ? { ...m, reactions: r.data.reactions, my_reactions: r.data.my_reactions } : m)));
    } catch { /* ignore */ }
  };
  const report = async (mid) => {
    if (!user) return;
    try { await API.post(`/chat/messages/${mid}/report`); toast.success("Reported to moderators 🙏"); }
    catch { /* ignore */ }
  };

  return (
    <div className="bg-[#FAF7F2] min-h-screen" data-testid="sakhi-chat-page">
      <Seo title="Sakhi Chat | Suvikka Community" description="Live chat rooms where Sakhis talk shopping, looks, products and what's trending — with Suvikka creators." canonicalPath="/sakhi-chat" />

      <section className="bg-[#2A1418] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#F0C97B]">Sakhi Chat · Live</p>
          <h1 className="font-display text-3xl sm:text-5xl font-bold mt-3">Talk To The Sakhis.</h1>
          <p className="text-white/75 mt-3 max-w-xl">Jump into a room and chat live about shopping, looks, products and what's trending — our creators hang out here too.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="chat-rooms">
          <div className="flex lg:flex-col gap-2.5 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide">
            {rooms.map((r) => {
              const Icon = ROOM_ICONS[r.id] || MessageCircle;
              const sel = r.id === active;
              const unread = Math.max(0, (r.message_count || 0) - (seen[r.id] || 0));
              return (
                <button key={r.id} onClick={() => setActive(r.id)} data-testid={`chat-room-${r.id}`}
                  className={`relative shrink-0 lg:w-full text-left rounded-2xl border p-4 transition-all ${sel ? "border-[#6A1B29] bg-white shadow-soft" : "border-[#EADBD8] bg-white/60 hover:bg-white hover:border-[#6A1B29]/40"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-10 h-10 grid place-items-center rounded-xl shrink-0 ${sel ? "bg-[#6A1B29] text-white" : "bg-[#F9ECE8] text-[#6A1B29]"}`}><Icon className="w-5 h-5" /></span>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#231518] text-sm whitespace-nowrap lg:whitespace-normal">{r.name}</p>
                      <p className="text-xs text-[#755C62] line-clamp-1 hidden lg:block">{r.desc}</p>
                    </div>
                  </div>
                  {!sel && unread > 0 && (
                    <span className="absolute top-3 right-3 min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-[#6A1B29] text-white text-[10px] font-bold" data-testid={`chat-unread-${r.id}`}>{unread > 9 ? "9+" : unread}</span>
                  )}
                </button>
              );
            })}
          </div>
          <Link to="/creators" data-testid="chat-meet-creators" className="hidden lg:flex items-center gap-2 mt-4 text-sm font-semibold text-[#6A1B29] hover:text-[#501325]">
            <Crown className="w-4 h-4" /> Meet the Creators
          </Link>
        </aside>

        <section className="bg-white border border-[#EADBD8] rounded-3xl shadow-soft flex flex-col h-[70vh] min-h-[460px] overflow-hidden" data-testid="chat-panel">
          <div className="px-5 py-4 border-b border-[#EADBD8] flex items-center gap-3">
            {(() => { const Icon = ROOM_ICONS[active] || MessageCircle; return <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#6A1B29] text-white"><Icon className="w-4 h-4" /></span>; })()}
            <div>
              <p className="font-display text-lg font-semibold text-[#231518] leading-tight">{activeRoom?.name || "Sakhi Chat"}</p>
              <p className="text-xs text-[#755C62]">{activeRoom?.desc}</p>
            </div>
          </div>

          {live.length > 0 && (
            <div className="bg-[#2A1418] text-white px-5 py-3 flex items-center gap-3" data-testid="chat-live-banner">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#6A1B29] px-2.5 py-1 rounded-full shrink-0"><Radio className="w-3.5 h-3.5 animate-pulse" /> Live</span>
              <p className="text-sm min-w-0 flex-1 truncate"><span className="font-semibold">{live[0].name || live[0].handle}</span>{live[0].title ? ` — ${live[0].title}` : " is hosting a live chat"}{live[0].at ? ` · ${live[0].at}` : ""}</p>
              {active !== "creators" && <button onClick={() => setActive("creators")} className="text-xs font-semibold text-[#F0C97B] hover:text-white whitespace-nowrap shrink-0" data-testid="chat-live-join">Join →</button>}
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="h-full grid place-items-center text-center">
                <div>
                  <MessageCircle className="w-8 h-8 text-[#6A1B29] mx-auto" />
                  <p className="font-display text-lg text-stone-800 mt-3">No messages yet.</p>
                  <p className="text-sm text-stone-500 mt-1">Be the first to start the conversation.</p>
                </div>
              </div>
            ) : (
              messages.map((m) => {
                const mine = user && m.user_id === user.id;
                return (
                  <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
                    <Avatar name={m.user_name} avatar={m.avatar} creator={m.is_creator} />
                    <div className={`max-w-[80%] ${mine ? "items-end text-right" : ""} flex flex-col`}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {m.username ? (
                          <Link to={`/sakhi/${m.username}`} className="text-xs font-semibold text-[#231518] hover:text-[#6A1B29]">{m.user_name}</Link>
                        ) : (
                          <span className="text-xs font-semibold text-[#231518]">{m.user_name}</span>
                        )}
                        {m.is_creator && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#B08A2E] bg-[#FDF5E6] border border-[#EAD9A8] rounded-full px-1.5 py-0.5" data-testid="chat-creator-badge"><BadgeCheck className="w-3 h-3" /> Creator</span>
                        )}
                        {m.pinned && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#6A1B29] bg-[#FFF0F4] border border-[#F3C9D6] rounded-full px-1.5 py-0.5" data-testid="chat-pinned"><Pin className="w-3 h-3" /> Pinned</span>
                        )}
                        <span className="text-[10px] text-stone-400">{timeAgo(m.created_at)}</span>
                      </div>
                      <div className={`mt-1 inline-block rounded-2xl overflow-hidden ${mine ? "bg-[#6A1B29] text-white rounded-tr-sm" : "bg-[#F6EFEA] text-[#231518] rounded-tl-sm"}`}>
                        {m.image && <img src={m.image} alt="" className="w-full max-w-[240px] object-cover" loading="lazy" />}
                        {m.body && <p className="px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words">{m.body}</p>}
                      </div>
                      {m.product && (
                        <Link to={`/product/${m.product.slug || m.product.id}`} data-testid={`chat-shop-${m.product.id}`}
                          className={`mt-1.5 inline-flex items-center gap-2 bg-white border border-[#EADBD8] rounded-full pl-1.5 pr-3 py-1.5 hover:border-[#6A1B29] transition-colors ${mine ? "self-end" : ""}`}>
                          {m.product.image && <img src={m.product.image} alt="" className="w-7 h-7 rounded-full object-cover" />}
                          <span className="text-[11px] font-semibold text-stone-800 truncate max-w-[140px]">{m.product.title}</span>
                          <span className="text-[11px] font-bold text-[#6A1B29] whitespace-nowrap">{inr(m.product.selling_price)}</span>
                          <ShoppingBag className="w-3.5 h-3.5 text-[#6A1B29]" />
                        </Link>
                      )}
                      <div className={`flex items-center gap-1.5 mt-1 flex-wrap ${mine ? "justify-end" : ""}`} data-testid={`chat-reactions-${m.id}`}>
                        {Object.entries(m.reactions || {}).map(([emo, cnt]) => cnt > 0 && (
                          <button key={emo} onClick={() => react(m.id, emo)} data-testid={`chat-react-${m.id}-${emo}`}
                            className={`inline-flex items-center gap-0.5 text-xs rounded-full px-2 py-0.5 border transition-colors ${(m.my_reactions || []).includes(emo) ? "bg-[#FFF0F4] border-[#6A1B29] text-[#6A1B29]" : "bg-white border-stone-200 text-stone-600 hover:border-[#6A1B29]"}`}>
                            <span>{emo}</span><span className="font-semibold">{cnt}</span>
                          </button>
                        ))}
                        {user && (
                          <div className="relative">
                            <button onClick={() => setPickerFor(pickerFor === m.id ? "" : m.id)} data-testid={`chat-react-btn-${m.id}`} className="text-stone-300 hover:text-[#6A1B29] p-0.5"><Smile className="w-4 h-4" /></button>
                            {pickerFor === m.id && (
                              <div className="absolute z-20 bottom-7 left-0 bg-white border border-stone-200 rounded-full shadow-lift px-2 py-1 flex gap-1.5" data-testid={`chat-emoji-picker-${m.id}`}>
                                {REACTIONS.map((emo) => <button key={emo} onClick={() => react(m.id, emo)} className="text-lg leading-none hover:scale-125 transition-transform">{emo}</button>)}
                              </div>
                            )}
                          </div>
                        )}
                        {user && !mine && (
                          <button onClick={() => report(m.id)} data-testid={`chat-report-${m.id}`} className="text-stone-200 hover:text-red-500 p-0.5" title="Report message"><Flag className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {user ? (
            <div className="border-t border-[#EADBD8]" data-testid="chat-composer">
              {(attachImg || attachProd) && (
                <div className="flex items-center gap-2 px-3 pt-3 flex-wrap" data-testid="chat-attach-preview">
                  {attachImg && (
                    <div className="relative">
                      <img src={attachImg} alt="" className="w-14 h-14 rounded-xl object-cover border border-[#EADBD8]" />
                      <button onClick={() => setAttachImg("")} className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-stone-900 text-white" data-testid="chat-remove-img"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                  {attachProd && (
                    <div className="relative inline-flex items-center gap-2 bg-[#FFF7FA] border border-[#EADBD8] rounded-full pl-1.5 pr-3 py-1.5">
                      {attachProd.image && <img src={attachProd.image} alt="" className="w-7 h-7 rounded-full object-cover" />}
                      <span className="text-[11px] font-semibold text-stone-800 truncate max-w-[120px]">{attachProd.title}</span>
                      <button onClick={() => setAttachProd(null)} className="text-stone-400 hover:text-red-500" data-testid="chat-remove-prod"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              )}
              {showProd && (
                <div className="px-3 pt-3" data-testid="chat-prod-picker">
                  <select onChange={(e) => { const p = products.find((x) => x.id === e.target.value); if (p) { setAttachProd({ id: p.id, title: p.title, image: (p.images || [])[0] || "", slug: p.slug, selling_price: p.selling_price }); setShowProd(false); } }}
                    className="w-full border border-[#EADBD8] rounded-lg px-3 py-2 text-sm" defaultValue="">
                    <option value="" disabled>Tag a product to shop…</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.title} — {inr(p.selling_price)}</option>)}
                  </select>
                </div>
              )}
              <form onSubmit={send} className="p-3 flex items-center gap-1.5">
                <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" data-testid="chat-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chat-attach-image"
                  className="w-10 h-10 grid place-items-center rounded-full text-[#6A1B29] hover:bg-[#F9ECE8] disabled:opacity-50 shrink-0" title="Add photo"><ImagePlus className="w-5 h-5" /></button>
                <button type="button" onClick={() => setShowProd((v) => !v)} data-testid="chat-attach-product"
                  className={`w-10 h-10 grid place-items-center rounded-full shrink-0 ${showProd ? "bg-[#F9ECE8] text-[#6A1B29]" : "text-[#6A1B29] hover:bg-[#F9ECE8]"}`} title="Tag a product"><Tag className="w-5 h-5" /></button>
                <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
                  placeholder={uploading ? "Uploading photo…" : `Message ${activeRoom?.name || "the Sakhis"}…`} data-testid="chat-input"
                  className="flex-1 bg-[#FAF7F2] border border-[#EADBD8] rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#6A1B29]" />
                <button type="submit" disabled={sending || uploading || (!text.trim() && !attachImg)} data-testid="chat-send"
                  className="w-11 h-11 grid place-items-center rounded-full bg-[#6A1B29] hover:bg-[#501325] text-white disabled:opacity-50 transition-colors shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          ) : (
            <div className="border-t border-[#EADBD8] p-4 text-center" data-testid="chat-login-prompt">
              <p className="text-sm text-stone-600">You can read along anytime. <span className="font-semibold text-[#231518]">Log in to join the chat.</span></p>
              <Link to="/login" className="inline-flex items-center gap-1.5 mt-3 bg-[#6A1B29] hover:bg-[#501325] text-white text-sm font-semibold px-6 py-2.5 rounded-full" data-testid="chat-login-cta"><LogIn className="w-4 h-4" /> Log in to chat</Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
